import ky, { type KyRequest } from 'ky';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';
const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';

let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;

interface RefreshTokenResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
}

async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);

  if (!refreshToken) {
    return null;
  }

  try {
    const response = await ky
      .post(`${API_URL}/auth/refresh`, {
        json: { refreshToken },
        headers: { 'Content-Type': 'application/json' },
      })
      .json<RefreshTokenResponse>();

    localStorage.setItem(ACCESS_TOKEN_KEY, response.access_token);
    localStorage.setItem(REFRESH_TOKEN_KEY, response.refresh_token);

    return response.access_token;
  } catch {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    return null;
  }
}

async function handleRefreshToken(): Promise<string | null> {
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;
  refreshPromise = refreshAccessToken().finally(() => {
    isRefreshing = false;
    refreshPromise = null;
  });

  return refreshPromise;
}

export const api = ky.create({
  prefixUrl: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  hooks: {
    beforeRequest: [
      (request) => {
        const token = localStorage.getItem(ACCESS_TOKEN_KEY);
        if (token) {
          request.headers.set('Authorization', `Bearer ${token}`);
        }

        request.headers.set('Origin', window.location.origin);
      },
    ],
    afterResponse: [
      async (request: KyRequest, _options, response) => {
        if (response.status === 401) {
          const url = new URL(request.url);
          // Don't try to refresh on auth endpoints
          if (
            url.pathname.includes('/auth/login') ||
            url.pathname.includes('/auth/refresh')
          ) {
            return response;
          }

          const newAccessToken = await handleRefreshToken();

          if (newAccessToken) {
            // Retry the original request with new token
            request.headers.set('Authorization', `Bearer ${newAccessToken}`);
            return ky(request);
          }

          // Refresh failed, redirect to login
          localStorage.removeItem(ACCESS_TOKEN_KEY);
          localStorage.removeItem(REFRESH_TOKEN_KEY);
          window.location.href = '/login';
        }

        return response;
      },
    ],
  },
});
